import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Landing from '@/pages/Landing';
import AuthCallback from '@/pages/AuthCallback';
import CreatorDashboard from '@/pages/CreatorDashboard';
import CreatorProfile from '@/pages/CreatorProfile';
import BrandProfile from '@/pages/BrandProfile';
import BrandWaitlist from '@/pages/BrandWaitlist';
import BrandLogin from '@/pages/BrandLogin';
import BrandDashboard from '@/pages/BrandDashboard';
import BrandDiscovery from '@/pages/BrandDiscovery';
import BrandBookmarks from '@/pages/BrandBookmarks';
import PublicBrandBrowser from '@/pages/PublicBrandBrowser';
import AdminPanel from '@/pages/AdminPanel';
import AdminImport from '@/pages/AdminImport';
import AdminAnalytics from '@/pages/AdminAnalytics';
import AdminBrands from '@/pages/AdminBrands';
import Leaderboards from '@/pages/Leaderboards';
import Resources from '@/pages/Resources';
import ResourceArticle from '@/pages/ResourceArticle';
import NewThisWeek from '@/pages/NewThisWeek';
import OnboardingWizard from '@/pages/OnboardingWizard';
import ViralReport from '@/pages/ViralReport';
import TermsOfService from '@/pages/TermsOfService';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import { Toaster } from '@/components/ui/sonner';
import { useAnalytics, useFirstInteraction } from '@/hooks/useAnalytics';
import '@/App.css';

// Domain redirect component - redirects www to non-www
function DomainRedirect({ children }) {
  useEffect(() => {
    const hostname = window.location.hostname;
    
    // Redirect www.sponsiq.com to sponsiq.com (preserve path and query)
    if (hostname === 'www.sponsiq.com') {
      const newUrl = `https://sponsiq.com${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.replace(newUrl);
    }
  }, []);
  
  // Don't render children if we're about to redirect
  if (window.location.hostname === 'www.sponsiq.com') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-slate-500 text-sm">Redirecting...</p>
        </div>
      </div>
    );
  }
  
  return children;
}

// Analytics wrapper component
function AnalyticsProvider({ children }) {
  useAnalytics(); // Track page views automatically
  useFirstInteraction(); // Track first user interaction
  return children;
}

function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <AnalyticsProvider>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/dashboard" element={<CreatorDashboard />} />
        <Route path="/onboarding" element={<OnboardingWizard />} />
        <Route path="/creator/:creatorId" element={<CreatorProfile />} />
        <Route path="/brand/:brandId" element={<BrandProfile />} />
        <Route path="/report/:creatorId" element={<ViralReport />} />
        <Route path="/leaderboards" element={<Leaderboards />} />
        <Route path="/explore-brands" element={<PublicBrandBrowser />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/resources/:slug" element={<ResourceArticle />} />
        <Route path="/new-this-week" element={<NewThisWeek />} />
        <Route path="/brands/join" element={<BrandWaitlist />} />
        <Route path="/brands/login" element={<BrandLogin />} />
        <Route path="/brands/dashboard" element={<BrandDashboard />} />
        <Route path="/brands/discover" element={<BrandDiscovery />} />
        <Route path="/brands/bookmarks/:brandEmail" element={<BrandBookmarks />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/admin/import" element={<AdminImport />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/admin/brands" element={<AdminBrands />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
      </Routes>
    </AnalyticsProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <DomainRedirect>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" />
          </BrowserRouter>
        </DomainRedirect>
      </div>
    </HelmetProvider>
  );
}

export default App;
